import { doc, addDoc, collection, updateDoc, deleteDoc, deleteField, arrayUnion, getDocs } from 'firebase/firestore';
import { ref, getStorage, uploadBytes, deleteObject } from 'firebase/storage';

import { DocumentTypes } from '@labradorsports/constants';
import { safeToDate } from '@labradorsports/utils';

import firestore from '../../firebase-config.js';
import { DataTags, VideoDomains } from '../../shared/constants.js';
import { createApiSlice } from '../api.js';
import { mainApi } from '../main/api.js';
import { CustomMutationDefinition, CustomQueryDefinition } from '../types.js';

export const messagingApi = createApiSlice({
    loadFeedPosts: {
        type: 'query',
        extraOptions: {
            retryable: true,
        },
        isValid: ({ activeTeam }) => Boolean(activeTeam),
        query: ({ activeTeam }) => ({
            path: 'messaging/getFeedPosts',
            query: {
                teamId: activeTeam,
            },
        }),
        transformResponse: ({ posts }) => {
            return posts.map((post: any) => ({
                ...post,
                createdDate: new Date(post.createdDate),
                likes: Object.fromEntries(
                    Object.entries(post.likes ?? {}).map(([uid, value]) => [
                        uid,
                        typeof value === 'string' ? new Date(value) : value,
                    ])
                ),
                comments:
                    post.comments?.map((comment: any) => ({
                        ...comment,
                        createdDate: safeToDate(comment.createdDate),
                    })) ?? [],
            }));
        },
        providesTags: (result: any[]) => {
            const baseTags = [{ type: DataTags.POST, id: 'LIST' }];

            if (!result) {
                return baseTags;
            }

            return result.map(({ id }) => ({ type: DataTags.POST, id })).concat(baseTags);
        },
    } as CustomQueryDefinition<{ activeTeam: string }, any>,

    savePost: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['content'],
        },
        isValid: ({ activeTeam, uid }) => Boolean(activeTeam && uid),
        queryFn: async ({ uid, activeTeam, audience, eventId, content }, { dispatch }) => {
            const totalPost: any = {
                audience,
                eventId,
                content,
                likes: {},
                comments: [],
                createdDate: new Date(),
                author: uid,
            };

            const videoDomain = VideoDomains.find((domain) => content.includes(domain));
            if (videoDomain) {
                const videoDomainIdx = content.indexOf(videoDomain);
                const preUrlContent = content.slice(0, videoDomainIdx);
                const urlStartIdx = videoDomainIdx - (preUrlContent.length - preUrlContent.lastIndexOf('"')) + 1;
                const urlEndIdx = content.slice(videoDomainIdx).indexOf('"') + videoDomainIdx;
                const videoUrl = content.slice(urlStartIdx, urlEndIdx);

                const { data: videoDetails } = await dispatch(
                    mainApi.endpoints.getVideoDetails.initiate({ videoLink: videoUrl })
                );

                if (videoDetails) {
                    totalPost.videoUrl = videoUrl;
                    totalPost.videoDetails = videoDetails;
                }
            }

            const ref = await addDoc(collection(firestore, 'teams', activeTeam, 'posts'), totalPost);

            return {
                data: {
                    ...totalPost,
                    id: ref.id,
                },
            };
        },
        invalidatesTags: [{ type: DataTags.POST, id: 'LIST' }],
    } as CustomMutationDefinition<
        { uid: string; activeTeam: string; audience: string; eventId: string; content: string },
        any
    >,

    deletePost: {
        type: 'mutation',
        isValid: ({ activeTeam, postId }) => Boolean(activeTeam && postId),
        queryFn: async ({ activeTeam, postId }) => {
            await deleteDoc(doc(firestore, 'teams', activeTeam, 'posts', postId));

            return {
                data: postId,
            };
        },
        invalidatesTags: (result, error, { postId }) => [{ type: DataTags.POST, id: postId }],
    } as CustomMutationDefinition<{ activeTeam: string; postId: string }, any>,

    changeLike: {
        type: 'mutation',
        isValid: ({ activeTeam, uid, postId }) => Boolean(activeTeam && uid && postId),
        queryFn: async ({ uid, activeTeam, postId, liked }) => {
            await updateDoc(doc(firestore, 'teams', activeTeam, 'posts', postId), {
                // delete sentinel can only appear at "top level" of update
                [`likes.${uid}`]: liked ? new Date() : deleteField(),
            });
        },
        onQueryStarted: async ({ uid, activeTeam, postId, liked }, { dispatch }) => {
            const likedAt = new Date();

            dispatch(
                messagingApi.util.updateQueryData('loadFeedPosts', { activeTeam }, (draftPosts) => {
                    const targetPost = draftPosts.find((post) => post.id === postId);

                    if (liked) {
                        targetPost.likes[uid] = likedAt;
                    } else {
                        delete targetPost.likes[uid];
                    }
                })
            );
        },
    } as CustomMutationDefinition<{ uid: string; activeTeam: string; postId: string; liked: boolean }, any>,

    saveComment: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['content'],
        },
        isValid: ({ activeTeam, uid, postId }) => Boolean(activeTeam && uid && postId),
        queryFn: async ({ uid, activeTeam, postId, content }) => {
            const comment = {
                content,
                author: uid,
                createdDate: new Date(),
            };

            await updateDoc(doc(firestore, 'teams', activeTeam, 'posts', postId), {
                comments: arrayUnion(comment),
            });

            return {
                data: comment,
            };
        },
        invalidatesTags: (result, error, arg) => [{ type: DataTags.POST, id: arg.postId }],
    } as CustomMutationDefinition<{ uid: string; activeTeam: string; postId: string; content: string }, any>,

    deleteComment: {
        type: 'mutation',
        isValid: ({ activeTeam, postId }) => Boolean(activeTeam && postId),
        queryFn: async ({ activeTeam, postId, commentIdx }, { getState }) => {
            const state = getState();
            const selector = messagingApi.endpoints.loadFeedPosts.select({ activeTeam });
            const { data: posts } = selector(state);
            const postIdx = posts.findIndex((p) => p.id === postId);
            const post = posts[postIdx];

            const newComments = post.comments.slice();
            newComments.splice(commentIdx, 1);

            await updateDoc(doc(firestore, 'teams', activeTeam, 'posts', postId), {
                comments: newComments,
            });

            return {
                data: newComments,
            };
        },
        invalidatesTags: (result, error, arg) => [{ type: DataTags.POST, id: arg.postId }],
    } as CustomMutationDefinition<{ activeTeam: string; postId: string; commentIdx: number }, any>,

    loadTeamDocuments: {
        type: 'query',
        isValid: ({ activeTeam }) => Boolean(activeTeam),
        queryFn: async ({ activeTeam }) => {
            const documentsQuery = await getDocs(collection(firestore, 'teams', activeTeam, 'documents'));
            const documents = documentsQuery.docs.map((doc) => {
                const data = doc.data();

                return {
                    id: doc.id,
                    ...data,
                    uploadedAt: data.uploadedAt.toDate(),
                    acknowledgements: Object.fromEntries(
                        Object.entries(data.acknowledgements ?? {}).map(([uid, timestamp]: any) => [
                            uid,
                            timestamp.toDate(),
                        ])
                    ),
                };
            });

            return {
                data: documents,
            };
        },
        providesTags: (result: any[]) => {
            const baseTags = [{ type: DataTags.TEAM_DOCUMENT, id: 'LIST' }];

            if (!result) {
                return baseTags;
            }

            return result.map(({ id }) => ({ type: DataTags.TEAM_DOCUMENT, id })).concat(baseTags);
        },
    } as CustomQueryDefinition<{ activeTeam: string }, any>,

    saveTeamDocument: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['document'],
        },
        isValid: ({ activeTeam, uid, document }) => Boolean(activeTeam && uid && document),
        queryFn: async ({ uid, document, activeTeam }, { extra }) => {
            const { logger } = extra;
            const { type, file, ...rest } = document;

            const docUpdate = {
                type,
                ...rest,
                uploadedBy: uid,
                uploadedAt: new Date(),
                acknowledgements: {},
            };

            if (document.id && type === DocumentTypes.FILE) {
                const storageRef = ref(getStorage(), document.path);

                try {
                    await deleteObject(storageRef);
                } catch (err) {
                    logger.log('deleteObject failed', { error: err.name });
                }
            }

            if (type === DocumentTypes.FILE) {
                const storageRef = ref(getStorage(), `teams/${activeTeam}/documents/${file.name}`);

                await uploadBytes(storageRef, file);

                docUpdate.fileName = file.name;
                docUpdate.path = storageRef.fullPath;
            }

            if (document.id) {
                await updateDoc(doc(firestore, 'teams', activeTeam, 'documents', document.id), docUpdate);
            } else {
                await addDoc(collection(firestore, 'teams', activeTeam, 'documents'), docUpdate);
            }
        },
        invalidatesTags: (result, error, arg) => [{ type: DataTags.TEAM_DOCUMENT, id: arg.document.id ?? 'LIST' }],
    } as CustomMutationDefinition<{ uid: string; activeTeam: string; document: any }, any>,

    deleteTeamDocument: {
        type: 'mutation',
        extraOptions: {
            unloggableArg: ['document.content'],
        },
        isValid: ({ activeTeam, document }) => Boolean(activeTeam && document),
        queryFn: async ({ document, activeTeam }, { extra }) => {
            const { logger } = extra;
            const { type, path, id } = document;

            if (type === DocumentTypes.FILE) {
                const storageRef = ref(getStorage(), path);

                try {
                    await deleteObject(storageRef);
                } catch (err) {
                    logger.log('deleteObject failed', { error: err.name });
                }
            }

            await deleteDoc(doc(firestore, 'teams', activeTeam, 'documents', id));
        },
        invalidatesTags: (result, error, arg) => [{ type: DataTags.TEAM_DOCUMENT, id: arg.document.id }],
    } as CustomMutationDefinition<{ activeTeam: string; document: any }, any>,

    acknowledgeTeamDocument: {
        type: 'mutation',
        query: ({ documentId, activeTeam }) => ({
            path: 'messaging/updateAcknowledgement',
            body: {
                documentIds: [documentId],
                teamId: activeTeam,
                acknowledged: true,
            },
        }),
        invalidatesTags: (result, error, arg) => [{ type: DataTags.TEAM_DOCUMENT, id: arg.documentId }],
    } as CustomMutationDefinition<{ activeTeam: string; documentId: string }, any>,

    getLinkPreview: {
        type: 'query',
        query: ({ url }) => ({
            path: 'messaging/urlPreview',
            query: {
                q: url,
            },
        }),
    } as CustomQueryDefinition<{ url: string }, any>,

    requestAcknowledgement: {
        type: 'mutation',
        query: ({ documentId, recipientUID, teamId }) => ({
            path: 'messaging/requestAcknowledgement',
            body: {
                documentId,
                recipientUID,
                teamId,
            },
        }),
    } as CustomMutationDefinition<{ documentId: string; recipientUID: string; teamId: string }, any>,
});

export const {
    useLoadFeedPostsQuery,
    useSavePostMutation,
    useDeletePostMutation,
    useChangeLikeMutation,
    useSaveCommentMutation,
    useDeleteCommentMutation,
    useLoadTeamDocumentsQuery,
    useSaveTeamDocumentMutation,
    useDeleteTeamDocumentMutation,
    useAcknowledgeTeamDocumentMutation,
    useGetLinkPreviewQuery,
    useRequestAcknowledgementMutation,
} = messagingApi;
